import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import NewRequestButton from '@/components/intake/NewRequestButton';
import {
  Inbox, BookOpen, Activity, Bell, Plus, ArrowRight, Clock,
  Package, ChevronRight
} from 'lucide-react';

const statCards = [
  { key: 'active_intakes', label: 'Active Intakes', icon: Inbox, color: '#98139C', path: '/backlog?state=CAPTURED,INTAKE_COMPLETE,REFINED,SCORED,SIZED' },
  { key: 'stories_in_progress', label: 'Stories in Progress', icon: BookOpen, color: '#10b981', path: '/sprint-board' },
  { key: 'sprint_health', label: 'Portfolio Health', icon: Activity, color: '#f59e0b', suffix: '%', path: '/metrics', hasBreakdown: true },
  { key: 'pending_approvals', label: 'Approvals Pending', icon: Bell, color: '#ef4444', path: '/approvals' },
];

const urgencyColors = { P0: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', P1: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', P2: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' };
const statusColors = { active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', in_development: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', maintenance: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', sunset: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400', planned: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' };

function ProductCard({ product, onClick }) {
  return (
    <button 
      onClick={onClick}
      className="w-full bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-5 flex items-center justify-between hover:border-[#98139C] dark:hover:border-[#98139C] transition-colors text-left group"
      data-testid={`product-card-${product.id}`}
    >
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 bg-[#f0dcf0] dark:bg-[#98139C]/20 flex items-center justify-center">
          <Package size={20} className="text-[#98139C]" />
        </div>
        <div>
          <h3 className="text-base font-medium text-[#30302F] dark:text-white">{product.name}</h3>
          <div className="flex items-center gap-3 mt-1">
            <Badge className={`text-[11px] rounded-none ${statusColors[product.status] || statusColors.active}`}>
              {product.status?.replace('_', ' ')}
            </Badge>
            <Badge className={`text-[11px] rounded-none ${urgencyColors[product.priority]}`}>
              {product.priority}
            </Badge>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-6">
        <div className="text-right hidden sm:block">
          <div className="text-sm font-medium text-[#30302F] dark:text-white">{product.total_story_points || 0}</div>
          <div className="text-[11px] text-[#30302F] dark:text-white">Story Points</div>
        </div>
        <div className="text-right hidden sm:block">
          <div className="text-sm font-medium text-[#30302F] dark:text-white">{product.story_count || 0}</div>
          <div className="text-[11px] text-[#30302F] dark:text-white">Stories</div>
        </div>
        <ChevronRight size={18} className="text-[#30302F] dark:text-white group-hover:text-[#98139C] transition-colors" />
      </div>
    </button>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentApprovals, setRecentApprovals] = useState([]);
  const [recentItems, setRecentItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [showNewProduct, setShowNewProduct] = useState(false);
  const [newProductName, setNewProductName] = useState('');
  const [creatingProduct, setCreatingProduct] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [statsRes, activityRes, approvalsRes, productsRes] = await Promise.all([
          api.get('/dashboard/stats'),
          api.get('/dashboard/recent-activity'),
          api.get('/approvals/', { params: { status: 'pending' } }),
          api.get('/products/')
        ]);
        setStats(statsRes.data);
        setRecentItems(activityRes.data.recent_items || []);
        setRecentApprovals(approvalsRes.data.slice(0, 5));
        setProducts(productsRes.data);
      } catch (err) { console.error('Dashboard load error:', err); }
    };
    load();
  }, []);

  const handleCreateProduct = async () => {
    if (!newProductName.trim()) return;
    setCreatingProduct(true);
    try {
      const res = await api.post('/products/', { name: newProductName.trim() });
      setProducts(prev => [res.data, ...prev]);
      setNewProductName('');
      setShowNewProduct(false);
      navigate(`/products/${res.data.id}`);
    } catch (err) { console.error(err); }
    finally { setCreatingProduct(false); }
  };

  // Get health color based on score
  const getHealthColor = (score) => {
    if (score >= 80) return '#10b981';
    if (score >= 60) return '#f59e0b';
    if (score >= 40) return '#f97316';
    return '#ef4444';
  };

  return (
    <div className="p-4 lg:p-8" data-testid="dashboard-page">
      <div className="mb-10">
        <p className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#98139C]">Dashboard</p>
        <h1 className="text-4xl font-heading text-[#30302F] dark:text-white mt-1" style={{ letterSpacing: '-0.8px' }}>
          Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 18 ? 'afternoon' : 'evening'}, {user?.name?.split(' ')[0]}
        </h1>
        <p className="text-[#30302F] dark:text-white mt-2 font-light text-lg">Here's what's happening across your products today.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10" data-testid="stats-grid">
        {statCards.map((card) => (
          <button 
            key={card.key} 
            onClick={() => navigate(card.path)}
            className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-6 text-left hover:border-[#98139C] dark:hover:border-[#98139C] transition-colors cursor-pointer group" 
            data-testid={`stat-card-${card.key}`}
          >
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium text-[#30302F] dark:text-white group-hover:text-[#98139C] transition-colors">{card.label}</span>
              <card.icon size={18} strokeWidth={1.5} style={{ color: card.hasBreakdown && stats ? getHealthColor(stats[card.key]) : card.color }} />
            </div>
            <div className="text-3xl font-heading" style={{ color: card.hasBreakdown && stats ? getHealthColor(stats[card.key]) : card.color }}>
              {stats ? `${stats[card.key]}${card.suffix || ''}` : '—'}
            </div>
          </button>
        ))}
      </div>

      {/* Product Portfolio Section */}
      <div className="mb-10" data-testid="product-portfolio-section">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-heading text-[#30302F] dark:text-white flex items-center gap-2">
              <Package size={24} className="text-[#98139C]" /> Product Portfolio
            </h2>
            <p className="text-sm text-[#30302F] dark:text-white font-light mt-1">
              {products.length} product{products.length !== 1 ? 's' : ''} • Click to view details
            </p>
          </div>
          <button 
            data-testid="add-product-btn"
            onClick={() => setShowNewProduct(true)}
            className="bg-[#98139C] hover:bg-[#7a0f7d] text-white rounded-full font-bold text-sm px-5 h-9 flex items-center gap-2 transition-colors"
          >
            <Plus size={14} /> Add Product
          </button>
        </div>

        {showNewProduct && (
          <div className="bg-white dark:bg-white/5 border border-[#98139C] p-4 mb-4" data-testid="new-product-form">
            <div className="flex items-center gap-3">
              <input
                type="text"
                value={newProductName}
                onChange={(e) => setNewProductName(e.target.value)}
                placeholder="Enter product name..."
                className="flex-1 h-10 px-4 border border-[#e3e3e3] dark:border-white/10 bg-white dark:bg-white/5 text-[#30302F] dark:text-white placeholder:text-[#30302F] dark:placeholder:text-white/30 focus:outline-none focus:border-[#98139C]"
                onKeyDown={(e) => e.key === 'Enter' && handleCreateProduct()}
                autoFocus
                data-testid="new-product-input"
              />
              <button
                onClick={handleCreateProduct}
                disabled={creatingProduct || !newProductName.trim()}
                className="bg-[#98139C] hover:bg-[#7a0f7d] text-white rounded-full font-bold text-sm px-5 h-10 transition-colors disabled:opacity-50"
                data-testid="create-product-btn"
              >
                {creatingProduct ? 'Creating...' : 'Create'}
              </button>
              <button
                onClick={() => { setShowNewProduct(false); setNewProductName(''); }}
                className="text-[#30302F] dark:text-white hover:text-[#30302F] dark:hover:text-white text-sm font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {products.length === 0 ? (
          <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 py-12 text-center">
            <Package size={40} className="mx-auto mb-3 text-[#30302F] dark:text-white" />
            <p className="text-[#30302F] dark:text-white font-heading">No products yet</p>
            <p className="text-sm text-[#30302F] dark:text-white mt-1 font-light">Create your first product to start organizing your work</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="product-list">
            {products.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                onClick={() => navigate(`/products/${product.id}`)}
              />
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10" data-testid="pending-approvals-card">
            <div className="p-6 border-b border-[#e3e3e3] dark:border-white/10 flex items-center justify-between">
              <h2 className="text-xl font-heading text-[#30302F] dark:text-white">Pending Approvals</h2>
              <button onClick={() => navigate('/approvals')} className="text-sm text-[#98139C] hover:underline font-medium flex items-center gap-1">
                View all <ArrowRight size={14} />
              </button>
            </div>
            <div className="p-6">
              {recentApprovals.length === 0 ? (
                <div className="text-center py-8 text-[#30302F] dark:text-white">
                  <Bell size={32} className="mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No pending approvals</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {recentApprovals.map((a) => (
                    <div key={a.id} data-testid={`approval-item-${a.id}`} className="p-4 border border-[#e3e3e3] dark:border-white/10 border-l-4 border-l-[#98139C] hover:bg-[#f9f5fa] dark:hover:bg-white/5 transition-colors cursor-pointer" onClick={() => navigate('/approvals')}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[#30302F] dark:text-white truncate">{a.title}</p>
                          <p className="text-xs text-[#30302F] dark:text-white mt-1 flex items-center gap-1">
                            <Clock size={11} /> {new Date(a.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 ml-3">
                          <Badge variant="outline" className={`text-[11px] rounded-none ${urgencyColors[a.urgency]}`}>{a.urgency}</Badge>
                          <Badge variant="outline" className="text-[11px] rounded-none bg-[#f0dcf0] dark:bg-[#98139C]/20 text-[#4F0077] dark:text-[#d87bdb] border-0">{a.gate}</Badge>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div>
          <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10" data-testid="quick-actions-card">
            <div className="p-6 border-b border-[#e3e3e3] dark:border-white/10">
              <h2 className="text-xl font-heading text-[#30302F] dark:text-white">Quick Actions</h2>
            </div>
            <div className="p-6 space-y-3">
              <NewRequestButton
                user={user}
                testId="new-intake-action"
                className="w-full bg-[#98139C] hover:bg-[#7a0f7d] text-white justify-start gap-2 h-11 rounded-full font-bold text-sm flex items-center px-6 transition-colors"
              />
              <button data-testid="view-backlog-action" onClick={() => navigate('/backlog')} className="w-full border-2 border-[#4F0077] dark:border-[#98139C] text-[#4F0077] dark:text-[#d87bdb] hover:bg-[#f9f5fa] dark:hover:bg-white/5 justify-start gap-2 h-11 rounded-full font-bold text-sm flex items-center px-6 transition-colors">
                <BookOpen size={16} /> View Backlog
              </button>
              <button data-testid="sprint-board-action" onClick={() => navigate('/sprint-board')} className="w-full border-2 border-[#4F0077] dark:border-[#98139C] text-[#4F0077] dark:text-[#d87bdb] hover:bg-[#f9f5fa] dark:hover:bg-white/5 justify-start gap-2 h-11 rounded-full font-bold text-sm flex items-center px-6 transition-colors">
                <Activity size={16} /> Sprint Board
              </button>
            </div>
          </div>

          {recentItems.length > 0 && (
            <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 mt-6" data-testid="recent-activity-card">
              <div className="p-6 border-b border-[#e3e3e3] dark:border-white/10">
                <h2 className="text-xl font-heading text-[#30302F] dark:text-white">Recent Activity</h2>
              </div>
              <div className="p-6">
                <div className="space-y-3">
                  {recentItems.slice(0, 5).map((item) => (
                    <div key={item.id} className="flex items-center gap-3 text-sm">
                      <div className="w-2 h-2 rounded-full bg-[#98139C]" />
                      <span className="flex-1 truncate text-[#30302F] dark:text-white font-light">{item.title}</span>
                      <Badge variant="outline" className="text-[11px] rounded-none dark:border-white/10 dark:text-white">{item.state}</Badge>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
